import React, { useEffect, useRef } from 'react';

const FloatingElements = () => {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const partRef   = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const TYPES = ['spark','petal','diamond','heart','orb','lotus'];

    // Read colors live — supports both dark and rose gold theme
    const C = () => {
      const rose = document.documentElement.classList.contains('theme-pink');
      return {
        gold:  rose ? '#e8a0b8' : '#c9a84c',
        rose:  rose ? '#f5c6d8' : '#e8557a',
        orb:   rose ? 'rgba(232,160,184,0.9)' : 'rgba(201,168,76,0.9)',
        fill:  rose ? 'rgba(232,160,184,0.12)' : 'rgba(201,168,76,0.12)',
      };
    };

    // Mobile devices get a much lighter particle load — smaller and fewer particles
    // so they don't overwhelm the small viewport. Small mobile (≤400px) = extra-sparse.
    const w = window.innerWidth;
    const isMobile = w <= 600;
    const isSmall = w <= 400;
    const sizeMul = isSmall ? 0.4 : (isMobile ? 0.5 : 1);
    const initialCount = isSmall ? 14 : (isMobile ? 18 : 55);
    const maxCount = isSmall ? 20 : (isMobile ? 24 : 70);

    const create = () => ({
      x: Math.random() * canvas.width,
      y: canvas.height + 20,
      size: (3 + Math.random() * 9) * sizeMul,
      vy: -(0.25 + Math.random() * 0.7),
      vx: (Math.random() - 0.5) * 0.35,
      opacity: 0.08 + Math.random() * 0.35,
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.025,
      type: TYPES[Math.floor(Math.random() * TYPES.length)],
      pulse: Math.random() * Math.PI * 2,
    });

    for (let i = 0; i < initialCount; i++) { const p = create(); p.y = Math.random() * canvas.height; partRef.current.push(p); }

    let frame = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      const c = C();
      if (frame % 20 === 0 && partRef.current.length < maxCount) partRef.current.push(create());

      partRef.current = partRef.current.filter(p => {
        p.y += p.vy; p.x += p.vx; p.rot += p.rotV; p.pulse += 0.025;
        const op = p.opacity * (0.7 + 0.3 * Math.sin(p.pulse));
        ctx.save();

        if (p.type === 'spark') {
          ctx.globalAlpha = op; ctx.strokeStyle = c.gold; ctx.lineWidth = 0.8;
          ctx.beginPath();
          for (let i = 0; i < 4; i++) { const a = (i/4)*Math.PI*2; ctx.moveTo(p.x,p.y); ctx.lineTo(p.x+Math.cos(a)*p.size*1.4, p.y+Math.sin(a)*p.size*1.4); }
          ctx.stroke();
          ctx.fillStyle = c.gold; ctx.beginPath(); ctx.arc(p.x,p.y,p.size*0.28,0,Math.PI*2); ctx.fill();

        } else if (p.type === 'diamond') {
          ctx.globalAlpha = op; ctx.translate(p.x,p.y); ctx.rotate(p.rot);
          ctx.strokeStyle = c.gold; ctx.lineWidth = 0.7; ctx.fillStyle = c.fill;
          ctx.beginPath(); ctx.moveTo(0,-p.size); ctx.lineTo(p.size*0.55,0); ctx.lineTo(0,p.size); ctx.lineTo(-p.size*0.55,0); ctx.closePath(); ctx.fill(); ctx.stroke();

        } else if (p.type === 'petal') {
          ctx.globalAlpha = op*0.75; ctx.translate(p.x,p.y); ctx.rotate(p.rot);
          ctx.fillStyle = c.rose; ctx.beginPath(); ctx.ellipse(0,-p.size*0.45,p.size*0.3,p.size*0.65,0,0,Math.PI*2); ctx.fill();

        } else if (p.type === 'heart') {
          ctx.globalAlpha = op*0.85; ctx.translate(p.x,p.y); ctx.rotate(p.rot);
          const s = p.size*0.38; ctx.fillStyle = c.rose;
          ctx.beginPath(); ctx.moveTo(0,s*1.2); ctx.bezierCurveTo(-s*2.5,s*0.4,-s*2.8,-s*1.2,0,-s*0.5); ctx.bezierCurveTo(s*2.8,-s*1.2,s*2.5,s*0.4,0,s*1.2); ctx.fill();

        } else if (p.type === 'orb') {
          ctx.globalAlpha = op*0.35;
          const g = ctx.createRadialGradient(p.x,p.y,0,p.x,p.y,p.size*2.2);
          g.addColorStop(0,c.orb); g.addColorStop(1,'transparent');
          ctx.fillStyle = g; ctx.beginPath(); ctx.arc(p.x,p.y,p.size*2.2,0,Math.PI*2); ctx.fill();

        } else if (p.type === 'lotus') {
          ctx.globalAlpha = op*0.65; ctx.translate(p.x,p.y); ctx.rotate(p.rot);
          for (let i=0;i<6;i++) { ctx.save(); ctx.rotate((i/6)*Math.PI*2); ctx.fillStyle = i%2===0?c.rose:c.gold; ctx.beginPath(); ctx.ellipse(0,-p.size*0.75,p.size*0.22,p.size*0.55,0,0,Math.PI*2); ctx.fill(); ctx.restore(); }
        }

        ctx.restore();
        return p.y > -30;
      });
      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:0, opacity:0.9 }} />;
};

export default FloatingElements;
